import { ThumbsUp } from 'lucide-react';
import * as Icons from 'lucide-react';
import { useVariantReasonVotes } from '@/hooks/useVariantReasonVotes';
import { cn } from '@/lib/utils';

interface Props {
  variantId: string;
}

function toPascal(slug: string): string {
  return slug
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join('');
}

function ReasonIcon({ name, className }: { name: string; className?: string }) {
  const Comp = (Icons as any)[toPascal(name)] as React.ComponentType<{ className?: string }> | undefined;
  if (!Comp) return <ThumbsUp className={className} />;
  return <Comp className={className} />;
}

export function VariantReasonsVote({ variantId }: Props) {
  const { data, isLoading, toggle, isPending } = useVariantReasonVotes(variantId);

  const handleClick = (slug: string) => {
    toggle(slug);
  };

  const total = data?.totalVoters ?? 0;
  const reasons = data?.reasons ?? [];
  const sorted = [...reasons].sort((a, b) => b.count - a.count);
  const top = sorted.filter((r) => r.count > 0).slice(0, 3);

  return (
    <div className="bg-card rounded-xl shadow-card p-5 space-y-4">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-display font-semibold text-lg">Hvorfor vælge denne bil?</h3>
        <span className="text-xs text-muted-foreground">
          {total === 0 ? 'Ingen stemmer endnu' : `${total} ${total === 1 ? 'bruger' : 'brugere'}`}
        </span>
      </div>

      {top.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Top grunde</p>
          {top.map((r) => {
            const pct = total > 0 ? Math.round((r.count / total) * 100) : 0;
            return (
              <div key={r.reason.id} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <ReasonIcon name={r.reason.icon} className="w-4 h-4 text-accent" />
                    {r.reason.label}
                  </span>
                  <span className="font-semibold tabular-nums">{pct}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-accent" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          Vælg de grunde du er enig i
        </p>
        <div className="flex flex-wrap gap-2">
          {isLoading && <span className="text-sm text-muted-foreground">Indlæser…</span>}
          {reasons.map((r) => (
            <button
              key={r.reason.id}
              type="button"
              onClick={() => handleClick(r.reason.slug)}
              disabled={isPending}
              aria-pressed={r.voted}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-sm border transition-colors',
                r.voted
                  ? 'bg-accent text-accent-foreground border-accent'
                  : 'bg-transparent text-foreground border-border hover:border-accent/40 hover:bg-accent/5',
                isPending && 'opacity-60 cursor-not-allowed',
              )}
            >
              <ReasonIcon name={r.reason.icon} className="w-3.5 h-3.5" />
              <span>{r.reason.label}</span>
              {r.count > 0 && (
                <span className={cn('text-xs tabular-nums', r.voted ? 'opacity-90' : 'text-muted-foreground')}>
                  {r.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
